import React from 'react';
import styled, { css } from 'styled-components';

export type ButtonVariant = 'primary' | 'secondary' | 'accent' | 'outline' | 'text';
export type ButtonSize = 'small' | 'medium' | 'large';

interface ButtonProps {
  variant?: ButtonVariant;
  size?: ButtonSize;
  fullWidth?: boolean;
  as?: React.ElementType;
  to?: string;
  children: React.ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  type?: 'button' | 'submit' | 'reset';
}

const getButtonStyles = (variant: ButtonVariant = 'primary', theme: any) => {
  const styles = {
    primary: css`
      background-color: ${theme.colors.primary};
      color: ${theme.colors.light};
      border: 2px solid ${theme.colors.primary};
      
      &:hover:not(:disabled), &:focus:not(:disabled) {
        background-color: transparent;
        color: ${theme.colors.primary};
      }
    `,
    secondary: css`
      background-color: ${theme.colors.secondary};
      color: ${theme.colors.light};
      border: 2px solid ${theme.colors.secondary};
      
      &:hover:not(:disabled), &:focus:not(:disabled) {
        background-color: transparent;
        color: ${theme.colors.secondary};
      }
    `,
    accent: css`
      background-color: ${theme.colors.accent};
      color: ${theme.colors.light};
      border: 2px solid ${theme.colors.accent};
      
      &:hover:not(:disabled), &:focus:not(:disabled) {
        background-color: transparent;
        color: ${theme.colors.accent};
      }
    `,
    outline: css`
      background-color: transparent;
      color: ${theme.colors.primary};
      border: 2px solid ${theme.colors.primary};
      
      &:hover:not(:disabled), &:focus:not(:disabled) {
        background-color: ${theme.colors.primary};
        color: ${theme.colors.light};
      }
    `,
    text: css`
      background-color: transparent;
      color: ${theme.colors.primary};
      border: 2px solid transparent;
      padding-left: 0;
      padding-right: 0;
      
      &:hover:not(:disabled), &:focus:not(:disabled) {
        color: ${theme.colors.accent};
        background-color: transparent;
      }
    `,
  };

  return styles[variant] || styles.primary;
};

const getButtonSize = (size: ButtonSize = 'medium', theme: any) => {
  const sizes = {
    small: css`
      padding: ${theme.spacing.xs} ${theme.spacing.md};
      font-size: ${theme.fontSizes.sm};
    `,
    medium: css`
      padding: ${theme.spacing.sm} ${theme.spacing.lg};
      font-size: ${theme.fontSizes.md};
    `,
    large: css`
      padding: ${theme.spacing.md} ${theme.spacing.xl};
      font-size: ${theme.fontSizes.lg};
    `,
  };

  return sizes[size] || sizes.medium;
};

const StyledButton = styled.button<ButtonProps>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-weight: ${props => props.theme.fontWeights.semiBold};
  border-radius: ${props => props.theme.borderRadius.md};
  cursor: pointer;
  transition: ${props => props.theme.transitions.medium};
  text-decoration: none;
  text-align: center;
  line-height: 1.2;
  width: ${props => props.fullWidth ? '100%' : 'auto'};
  
  ${props => getButtonStyles(props.variant, props.theme)}
  ${props => getButtonSize(props.size, props.theme)}
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
  
  svg {
    margin-right: ${props => props.theme.spacing.xs};
  }
`;

const Button: React.FC<ButtonProps> = ({
  children,
  variant = 'primary',
  size = 'medium',
  fullWidth = false,
  type = 'button',
  ...props
}) => {
  return (
    <StyledButton
      variant={variant}
      size={size}
      fullWidth={fullWidth}
      type={type}
      {...props}
    >
      {children}
    </StyledButton>
  );
};

export default Button; 